import { resolveDestinationScopeFromPackageId } from "./esimCatalog";

/** Public API origin (api.albavox.com). No trailing slash. */
const API_ROOT = (import.meta.env.VITE_API_BASE_URL ?? "https://api.albavox.com").replace(
  /\/$/,
  "",
);

/** CMS (waitlist, places) — nginx /cms or absolute. */
export const CMS_BASE = (
  import.meta.env.VITE_CMS_API_BASE ?? `${API_ROOT}/cms`
).replace(/\/$/, "");

/** External services (eSIM packages). */
export const EXTERNAL_BASE = (
  import.meta.env.VITE_EXTERNAL_API_BASE ?? `${API_ROOT}/external`
).replace(/\/$/, "");

/** Payments (guest eSIM checkout). */
export const PAYMENTS_BASE = (
  import.meta.env.VITE_PAYMENTS_API_BASE ?? `${API_ROOT}/payments`
).replace(/\/$/, "");

/** @deprecated use CMS_BASE — kept for older imports */
const API_BASE = CMS_BASE;

export type WaitlistSubscribePayload = {
  firstName: string;
  lastName: string;
  email: string;
  languageCode?: string;
  source?: string;
};

export type WaitlistSubscribeResult = {
  id: number;
  alreadySubscribed: boolean;
};

export async function subscribeToWaitlist(
  payload: WaitlistSubscribePayload,
): Promise<WaitlistSubscribeResult> {
  const response = await fetch(`${CMS_BASE}/api/v1/waitlist/subscribe`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = "Something went wrong. Please try again.";
    try {
      const data = (await response.json()) as {
        message?: string;
        failures?: Record<string, string[]>;
      };
      if (data.message) message = data.message;
      else if (data.failures) {
        const first = Object.values(data.failures).flat()[0];
        if (first) message = first;
      }
    } catch {
      /* ignore parse errors */
    }
    throw new Error(message);
  }

  return (await response.json()) as WaitlistSubscribeResult;
}

export type MapPlacePin = {
  id: number;
  attractionId: string;
  name: string;
  latitude: number;
  longitude: number;
  imageUrl: string | null;
};

type CountryPlacePayload = {
  id: number;
  attractionId: string;
  name: string;
  latitude: string | number;
  longitude: string | number;
  placeCategory?: string | number;
  mainImageUrl?: string | null;
  photos?: Array<{ url: string; isPrimary?: boolean | null }>;
};

function parseCoordinate(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toUpperCase() === "N/A") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function usableImageUrl(url: string | null | undefined): string | null {
  if (!url || url.trim() === "" || url.trim().toUpperCase() === "N/A") {
    return null;
  }
  return url;
}

function toMapPin(place: CountryPlacePayload): MapPlacePin | null {
  const latitude = parseCoordinate(place.latitude);
  const longitude = parseCoordinate(place.longitude);
  if (latitude == null || longitude == null) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  const category = place.placeCategory;
  if (
    category != null &&
    category !== "PlaceToVisit" &&
    category !== 1
  ) {
    return null;
  }

  const primaryPhoto = place.photos?.find((photo) => photo.isPrimary)?.url
    ?? place.photos?.[0]?.url;

  return {
    id: place.id,
    attractionId: place.attractionId,
    name: place.name,
    latitude,
    longitude,
    imageUrl: usableImageUrl(primaryPhoto) ?? usableImageUrl(place.mainImageUrl),
  };
}

export async function getMapPlaces(
  countryId: number,
  acceptLanguage: string,
  signal?: AbortSignal,
): Promise<MapPlacePin[]> {
  const headers = {
    Accept: "application/json",
    "Accept-Language": acceptLanguage,
  };

  const mapResponse = await fetch(
    `${CMS_BASE}/api/v1/places/map?countryId=${encodeURIComponent(String(countryId))}`,
    { method: "GET", headers, signal },
  ).catch(() => null);

  if (mapResponse?.ok) {
    const data = (await mapResponse.json()) as MapPlacePin[];
    if (Array.isArray(data) && data.length > 0) {
      return data.filter(
        (pin) =>
          Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude),
      );
    }
  }

  const countryResponse = await fetch(
    `${CMS_BASE}/api/v1/places/byCountry/${encodeURIComponent(String(countryId))}`,
    { method: "GET", headers, signal },
  );

  if (!countryResponse.ok) {
    throw new Error("Failed to load map places.");
  }

  const places = (await countryResponse.json()) as CountryPlacePayload[];
  if (!Array.isArray(places)) return [];
  return places
    .map(toMapPin)
    .filter((pin): pin is MapPlacePin => pin != null);
}

export type EsimSignal = {
  code: string;
  name: string;
  networks?: string[] | null;
  carriers?: string[] | null;
};

export type EsimPackage = {
  id?: number;
  packageId: string;
  packageName?: string | null;
  plan?: string | null;
  activation?: string | null;
  durationDays: number;
  price: number;
  isUnlimited: boolean;
  dataAmount: number;
  dataUnit?: string | null;
  withHotspot?: boolean;
  withDataRoaming?: boolean;
  withSMS?: boolean;
  withCall?: boolean;
  withTopUp?: boolean;
  withUsageCheck?: boolean;
  withThrottle?: boolean;
  countryName?: string | null;
  countryCode?: string | null;
  countrySlug?: string | null;
  geography?: string | null;
  region?: string | null;
  imageUrl?: string | null;
  networks?: string[] | null;
  carriers?: string[] | null;
  signals?: EsimSignal[] | null;
  highSpeedAllowanceMb?: number | null;
  throttleSpeedKbps?: number | null;
  afterCapBehavior?: string | null;
  fairUsageSummary?: string | null;
};

export type EsimPackagesResponse = {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  packages: EsimPackage[];
  availableDurationDays?: number[];
};

export type FetchEsimPackagesParams = {
  countryCode?: string;
  regionalPackage?: string;
  geography?: string;
  page?: number;
  pageSize?: number;
  filter?: string;
  isUnlimited?: boolean;
  withCall?: boolean;
  withSMS?: boolean;
  withHotspot?: boolean;
  withDataRoaming?: boolean;
  withTopUp?: boolean;
  minDays?: number;
  maxDays?: number;
  minDataMb?: number;
  maxDataMb?: number;
  sortBy?: string;
  sortDesc?: boolean;
  /** Exact Roamify package id lookup (works without destination scope). */
  packageId?: string;
};

export async function fetchEsimPackages(
  params: FetchEsimPackagesParams,
  signal?: AbortSignal,
): Promise<EsimPackagesResponse> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });

  const response = await fetch(
    `${EXTERNAL_BASE}/api/ESim/packages?${query.toString()}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to load eSIM packages.");
  }

  return (await response.json()) as EsimPackagesResponse;
}

/** Load one package by id — used after refresh when navigation state is gone. */
export async function fetchEsimPackageById(
  packageId: string,
  signal?: AbortSignal,
): Promise<EsimPackage | null> {
  const pick = (data: EsimPackagesResponse) =>
    data.packages.find((p) => p.packageId === packageId) ?? null;

  // Newer APIs support exact packageId lookup (no destination scope needed).
  try {
    const byId = await fetchEsimPackages(
      { packageId, page: 0, pageSize: 1 },
      signal,
    );
    const exact = pick(byId);
    if (exact) return exact;
  } catch {
    // Older ExternalServices builds reject packageId-only queries — fall back below.
  }

  const scope = resolveDestinationScopeFromPackageId(packageId);
  if (!scope) return null;

  const params: FetchEsimPackagesParams = {
    page: 0,
    pageSize: 100,
  };
  if (scope.regionalPackage) {
    params.regionalPackage = scope.regionalPackage;
  } else if (scope.geography === "global" && scope.countryCode) {
    params.countryCode = scope.countryCode;
  } else if (scope.countryCode) {
    params.countryCode = scope.countryCode;
  } else if (scope.geography) {
    params.geography = scope.geography;
  }

  // Paginate until we find the package (some destinations have many plans).
  for (let page = 0; page < 20; page += 1) {
    const data = await fetchEsimPackages({ ...params, page }, signal);
    const exact = pick(data);
    if (exact) return exact;
    if (page + 1 >= (data.totalPages || 0) || data.packages.length === 0) {
      break;
    }
  }

  return null;
}

export type CreateGuestEsimCheckoutPayload = {
  packageId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  createAccount: boolean;
  successUrl: string;
  cancelUrl: string;
  idempotencyKey?: string;
};

export type CreateGuestEsimCheckoutResult = {
  orderId: string;
  checkoutUrl: string | null;
  message?: string;
};

export async function createGuestEsimCheckout(
  payload: CreateGuestEsimCheckoutPayload,
): Promise<CreateGuestEsimCheckoutResult> {
  const response = await fetch(`${PAYMENTS_BASE}/api/checkout/guest/esim`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = "Unable to start checkout.";
    try {
      const data = (await response.json()) as { error?: string; message?: string };
      message = data.error || data.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return (await response.json()) as CreateGuestEsimCheckoutResult;
}

export type GuestEsimOrderStatus = {
  orderId: string;
  status: string;
  amount: number;
  currency: string;
  packageId: string;
  fulfilledAtUtc?: string | null;
  lastError?: string | null;
  receiptAvailable?: boolean;
  install?: {
    qrCodeUrl?: string | null;
    lpaCode?: string | null;
    activationCode?: string | null;
    smdpAddress?: string | null;
    iosQuickInstall?: string | null;
    iccid?: string | null;
    provisioningStatus?: string | null;
  } | null;
};

export async function getGuestEsimOrder(
  orderId: string,
  email: string,
  signal?: AbortSignal,
): Promise<GuestEsimOrderStatus> {
  const response = await fetch(
    `${PAYMENTS_BASE}/api/checkout/guest/orders/${encodeURIComponent(orderId)}?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("Unable to load order status.");
  }

  return (await response.json()) as GuestEsimOrderStatus;
}

/** Same PDF receipt attached to the payment confirmation email. */
export async function downloadGuestEsimReceipt(
  orderId: string,
  email: string,
): Promise<void> {
  const response = await fetch(
    `${PAYMENTS_BASE}/api/checkout/guest/orders/${encodeURIComponent(orderId)}/receipt?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: { Accept: "application/pdf" },
    },
  );

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error("Receipt is not ready yet. Please try again in a moment.");
    }
    throw new Error("Unable to download receipt.");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  const fileName = match
    ? match[1].replace(/['"]/g, "")
    : `AlbaVOX_Receipt_${orderId.slice(0, 8)}.pdf`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export { API_BASE };
